// src/components/Utils/MDtoHTMLConverter.js
import React, { useState, useCallback, useEffect } from 'react';
import { ArrowLeft, ChevronLeft, Upload, Download, Copy, Check, Eye, EyeOff, Maximize2, Minimize2, Volume2, VolumeX, X } from 'lucide-react';
import { convertMarkdownToHTML, downloadAsHTML } from './MDtoHTML';
import './MDtoHTMLConverter.css';
import './common_Utils.css';
import ttsManager from './TTS';

const MDtoHTMLConverter = ({ onBack, user, userProfile }) => {
  const [markdown, setMarkdown] = useState('# Markdown to HTML 변환기\n\n여기에 **Markdown** 텍스트를 입력하거나 파일을 업로드하세요.\n\n## 예제\n- 목록 항목 1\n- 목록 항목 2\n\n**굵은 글씨**와 *기울임* 텍스트');
  const [htmlOutput, setHtmlOutput] = useState('');
  const [copied, setCopied] = useState(false);
  const [screenView, setScreenView] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [fullscreenInput, setFullscreenInput] = useState(false);
  const [fullscreenOutput, setFullscreenOutput] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  const [activeTab, setActiveTab] = useState('editor');

  // frontPane 상태 추가
  const [frontPane, setFrontPane] = useState('input');
  
  // 드래그 이동 관련 상태
  const [inputPosition, setInputPosition] = useState({ x: 0, y: 0 }); 
  const getInitialPreviewPosition = () => {
    // 에디터 컨테이너의 너비를 기준으로 50% 계산
    const containerWidth = window.innerWidth;
      return { x: containerWidth * 0.5, y: 0 };
    };
  const [outputPosition, setOutputPosition] = useState(getInitialPreviewPosition());

  const [draggingPane, setDraggingPane] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // 팝업 창 관련
  const [inputPopup, setInputPopup] = useState(null);
  const [outputPopup, setOutputPopup] = useState(null);

  // Markdown 변환

  // Markdown 변환
  const handleConvert = useCallback(() => {
    const html = convertMarkdownToHTML(markdown);
    setHtmlOutput(html);
  }, [markdown]);

  // 파일 업로드
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      readFile(file);
    }
  };

  // 파일 읽기
  const readFile = (file) => {
    if (!file.name.match(/\.(md|markdown|txt)$/i)) {
      alert('Markdown (.md, .markdown) 또는 텍스트 (.txt) 파일만 업로드할 수 있습니다.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setMarkdown(e.target.result);
      // 자동 변환
      setTimeout(() => {
        const html = convertMarkdownToHTML(e.target.result);
        setHtmlOutput(html);
      }, 100);
    };
    reader.readAsText(file);
  };

  // HTML 다운로드
  const handleDownloadHTML = () => {
    downloadAsHTML(markdown, 'converted_document.html');
  };

  // HTML 코드 복사
  const handleCopyHTML = async () => {
    try {
      await navigator.clipboard.writeText(htmlOutput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('복사 실패:', err);
      alert('복사에 실패했습니다.');
    }
  };

  // 드래그 앤 드롭
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      readFile(files[0]);
    }
  }, []);

  // Text-to-Speech 핸들러
  const handleTextToSpeech = () => {
    // HTML 출력이 없으면 알림
    if (!htmlOutput) {
      alert('먼저 Markdown을 HTML로 변환해주세요.');
      return;
    }

    const success = ttsManager.speak(
      htmlOutput,
      () => setIsSpeaking(true),  // onStart
      () => setIsSpeaking(false), // onEnd
      (error) => console.error('TTS 오류:', error) // onError
    );
    
    // 이미 실행 중이었다면 중지된 것이므로 상태 업데이트
    if (!success && isSpeaking) {
      setIsSpeaking(false);
    }
  };

  // 패널 드래그 시작 핸들러
  const handlePaneDragStart = (pane, e) => {
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('textarea')) {
      return;
    }
    
    e.preventDefault();
    setDraggingPane(pane);
    
    // 현재 드래그하는 pane의 실제 화면상 위치 가져오기
    const paneElement = e.currentTarget.parentElement; // section-header의 부모인 input-section 또는 output-section
    const rect = paneElement.getBoundingClientRect();
    
    // 드래그 시작 시 다른 pane의 현재 위치도 고정
    if (pane === 'input') {
      // output의 현재 위치 저장
      const outputPane = document.querySelector('.output-section');
      if (outputPane && (outputPosition.x === 0 && outputPosition.y === 0)) {
        const outputRect = outputPane.getBoundingClientRect();
        setOutputPosition({ x: outputRect.left, y: outputRect.top });
      }
    } else if (pane === 'output') {
      // input의 현재 위치 저장
      const inputPane = document.querySelector('.input-section');
      if (inputPane && (inputPosition.x === 0 && inputPosition.y === 0)) {
        const inputRect = inputPane.getBoundingClientRect();
        setInputPosition({ x: inputRect.left, y: inputRect.top });
      }
    }
    
    // 마우스 클릭 위치와 패널의 현재 위치 차이 계산
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  // 패널 드래그 중
  const handlePaneDrag = useCallback((e) => {
    if (!draggingPane) return;
    
    e.preventDefault();
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    if (draggingPane === 'input') {
      setInputPosition({ x: newX, y: newY });
    } else if (draggingPane === 'output') {
      setOutputPosition({ x: newX, y: newY });
    }
  }, [draggingPane, dragOffset]);

  // 패널 드래그 종료
  const handlePaneDragEnd = useCallback(() => {
    setDraggingPane(null);
  }, []);

  // 마우스 이벤트 리스너 등록
  useEffect(() => {
    if (draggingPane) {
      document.addEventListener('mousemove', handlePaneDrag);
      document.addEventListener('mouseup', handlePaneDragEnd);
      return () => {
        document.removeEventListener('mousemove', handlePaneDrag);
        document.removeEventListener('mouseup', handlePaneDragEnd);
      };
    }
  }, [draggingPane, handlePaneDrag, handlePaneDragEnd]);

  // 입력 팝업 창 열기
  const openInputInNewWindow = () => {

    const width = 1280;
    const height = 768;
    // 현재 브라우저 창이 있는 화면 기준으로 중앙 계산
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open('', 'InputPopup', `width=${width},height=${height},left=${left},top=${top}`); 

    // const popup = window.open('', 'InputPopup', 'width=800,height=600');
    if (popup) {
      popup.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Markdown 입력</title>
          <style>
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
            textarea { width: 100%; height: calc(100vh - 40px); border: 1px solid #ccc; padding: 10px; font-family: monospace; font-size: 14px; resize: none; }
          </style>
        </head>
        <body>
          <textarea id="markdownInput">${markdown.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</textarea>
          <script>
            const textarea = document.getElementById('markdownInput');
            textarea.addEventListener('input', (e) => {
              window.opener.postMessage({ type: 'updateMarkdown', value: e.target.value }, '*');
            });
            window.addEventListener('message', (e) => {
              if (e.data.type === 'syncMarkdown') {
                textarea.value = e.data.value;
              }
            });
          </script>
        </body>
        </html>
      `);
      setInputPopup(popup);
    }
  };

  // 출력 팝업 창 열기
  const openOutputInNewWindow = () => {
    const width = 1280;
    const height = 768;
    // 현재 브라우저 창이 있는 화면 기준으로 중앙 계산
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open('', 'OutputPopup', `width=${width},height=${height},left=${left},top=${top}`); 

    // const popup = window.open('', 'OutputPopup', 'width=800,height=600');
    if (popup) {
      popup.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>HTML 출력</title>
          <style>
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
          </style>
        </head>
        <body>
          <div id="output">${htmlOutput}</div>
        </body>
        </html>
      `);
      setOutputPopup(popup);
    }
  };

  // 팝업 창 메시지 리스너
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'updateMarkdown') {
        setMarkdown(event.data.value);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // markdown이 변경되면 팝업 창 동기화
  useEffect(() => {
    if (inputPopup && !inputPopup.closed) {
      inputPopup.postMessage({ type: 'syncMarkdown', value: markdown }, '*');
    }
  }, [markdown, inputPopup]);

  // htmlOutput이 변경되면 팝업 창 동기화
  useEffect(() => {
    if (outputPopup && !outputPopup.closed) {
      outputPopup.document.getElementById('output').innerHTML = htmlOutput;
    }
  }, [htmlOutput, outputPopup]);

  // 컴포넌트 언마운트 시 음성 중지
  useEffect(() => {
    return () => {
      ttsManager.stop();
    };
  }, []);

  return (
    <div 
      className={`md-to-html-converter ${isDragging ? 'dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="converter-header">
        <div 
          onClick={onBack}  
          className="common-back-button"
        >
          <ChevronLeft size={24} />
        </div>
        <h1>📝 Markdown → HTML 변환기</h1>
        <div className="header-actions">
          <label className="upload-btn">
            <Upload className="icon" />
            파일 업로드
            <input 
              type="file" 
              accept=".md,.markdown,.txt" 
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>
          <button onClick={() => setScreenView(!screenView)} className="action-btn">
            {screenView ? <EyeOff className="icon" /> : <Eye className="icon" />}
            {screenView ? 'Screen View' : 'Code View'}
          </button>
          <button onClick={handleCopyHTML} className="action-btn" disabled={!htmlOutput}>
            {copied ? <Check className="icon" /> : <Copy className="icon" />}
            {copied ? '복사됨!' : 'HTML 복사'}
          </button>
          <button onClick={handleDownloadHTML} className="action-btn download" disabled={!markdown.trim()}>
            <Download className="icon" />
            HTML 다운로드
          </button>
        </div>
      </div>

      <div className="tab-buttons"> 
        <button 
          className={`tab-btn ${activeTab === 'editor' ? 'active' : ''}`}
          onClick={() => setActiveTab('editor')}
        >
          📝 Editor
        </button>
        <button 
          className={`tab-btn ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => {setActiveTab('preview'); setOutputPosition({ x: 0, y: 0 }); }}
        >
          Preview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'both' ? 'active' : ''}`}
          onClick={() => setActiveTab('both')}
        >
          Both
        </button> 
      </div>

      <div className="converter-content">
        <div className="converter-grid">
          {(activeTab === 'editor' || activeTab === 'both') && (
          <div 
            className={`input-section ${fullscreenInput ? 'fullscreen' : ''} ${draggingPane === 'input' ? 'dragging-pane' : ''}`}
            style={{
              position: inputPosition.x !== 0 || inputPosition.y !== 0 ? 'absolute' : 'static',
              left: inputPosition.x !== 0 ? `${inputPosition.x}px` : 'auto',
              top: inputPosition.y !== 0 ? `${inputPosition.y}px` : 'auto',
              width: inputPosition.x !== 0 || inputPosition.y !== 0 ? '45%' : 'auto',
              height: inputPosition.x !== 0 || inputPosition.y !== 0 ? '600px' : 'auto',
              zIndex: (draggingPane === 'input' || frontPane === 'input') ? 1000 : 1
            }}
            onClick={() => setFrontPane('input')}
          >
            <div 
              className="section-header draggable-header"
              onMouseDown={(e) => handlePaneDragStart('input', e)}
              style={{ cursor: 'move' }}
            >
              <h3>📄 Markdown 입력</h3>
              <div className="header-right">
                <button 
                  className="popup-btn" 
                  onClick={openInputInNewWindow}
                  title="새 창으로 열기"
                >
                  <Eye className="icon" />
                </button>
                <span className="char-count">{markdown.length} 글자</span>
                <button 
                  className="editor-copy-btn" 
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(markdown);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    } catch (err) {
                      console.error('복사 실패:', err);
                    }
                  }}
                  title="입력 내용 복사"
                >
                  {copied ? <Check className="icon" /> : <Copy className="icon" />}
                </button> 
                <button 
                  className="editor-clear-btn" 
                  onClick={() => setMarkdown('')}
                  title="입력 내용 지우기"
                >
                  <X className="icon" />
                </button>
                {/* <button 
                  className="fullscreen-btn" 
                  onClick={() => setFullscreenInput(!fullscreenInput)}
                  title={fullscreenInput ? "축소" : "전체화면"}
                >
                  {fullscreenInput ? <Minimize2 className="icon" /> : <Maximize2 className="icon" />}
                </button> */}
              </div>
            </div>
            <textarea
              className="markdown-textarea"
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              placeholder="여기에 Markdown을 입력하거나 파일을 드래그 앤 드롭하세요..."
            />
            <button onClick={handleConvert} className="convert-btn">
              🔄 변환하기
            </button>
          </div>
          )}

          {(activeTab === 'preview' || activeTab === 'both') && (
          <div 
            className={`output-section ${fullscreenOutput ? 'fullscreen' : ''} ${draggingPane === 'output' ? 'dragging-pane' : ''}`}
            style={{
              position: outputPosition.x !== 0 || outputPosition.y !== 0 ? 'absolute' : 'static',
              left: outputPosition.x !== 0 ? `${outputPosition.x}px` : 'auto',
              top: outputPosition.y !== 0 ? `${outputPosition.y}px` : 'auto',
              width: outputPosition.x !== 0 || outputPosition.y !== 0 ? '45%' : 'auto',
              height: outputPosition.x !== 0 || outputPosition.y !== 0 ? '600px' : 'auto',
              zIndex: (draggingPane === 'output' || frontPane === 'output') ? 1000 : 1
            }}
            onClick={() => setFrontPane('output')}
          >
            <div 
              className="section-header draggable-header"
              onMouseDown={(e) => handlePaneDragStart('output', e)}
              style={{ cursor: 'move' }}
            >
              <h3>🎨 HTML 출력</h3>
              <div className="header-right">
                <button 
                  className="popup-btn" 
                  onClick={openOutputInNewWindow}
                  title="새 창으로 열기"
                >
                  <Eye className="icon" />
                </button>
                <span className="char-count">{htmlOutput.length} 글자</span>
                <button 
                  className="preview-copy-btn" 
                  onClick={handleCopyHTML}
                  title="HTML 출력 복사"
                  disabled={!htmlOutput}
                >
                  {copied ? <Check className="icon" /> : <Copy className="icon" />}
                </button>
                <button 
                  className="tts-btn" 
                  onClick={handleTextToSpeech}
                  title={isSpeaking ? "읽기 중지" : "읽어주기"}
                  disabled={!htmlOutput}
                >
                  {isSpeaking ? <VolumeX className="icon" /> : <Volume2 className="icon" />}
                </button>
                {/* <button 
                  className="fullscreen-btn" 
                  onClick={() => setFullscreenOutput(!fullscreenOutput)}
                  title={fullscreenOutput ? "축소" : "전체화면"}
                >
                  {fullscreenOutput ? <Minimize2 className="icon" /> : <Maximize2 className="icon" />}
                </button> */}
              </div>
            </div>
            
            {screenView ? (
              <div className="html-preview" dangerouslySetInnerHTML={{ __html: htmlOutput }} />
            ) : (
              <textarea
                className="html-textarea"
                value={htmlOutput}
                readOnly
                placeholder="변환된 HTML 코드가 여기에 표시됩니다..."
              />
            )}
          </div>
          )}
        </div>

        
      </div>

      {/* 드래그 오버레이 */}
      {isDragging && (
        <div className="drag-overlay">
          <div className="drag-message">
            <Upload size={48} />
            <p>여기에 Markdown 파일을 드롭하세요</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MDtoHTMLConverter;